import React, { useEffect } from 'react';
import { useDocumentTitle } from '../../shared/use-document-title';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Loader } from '../../components/Loader';
import { useSearchEmailForm } from '../../shared/use-search-email-form';
import { getEmailLeaks } from '../../../lib/use-cases/get-email-leaks';
import { useRouting } from '../../shared/use-routing';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './EmailCheckPage.css';

function EmailCheckPage() {
    useDocumentTitle('Email Checker');

    const {parseLocationSearch, location, navigate} = useRouting();
    const initialParams = parseLocationSearch(location.search);
    const {errors, fields, changeField, submitForm, clearForm, isPending, data, isSuccessful} = useSearchEmailForm(getEmailLeaks, () => logEvent(EVENT_TYPES.QUICK_CHECK_REQUESTED), initialParams);

    useEffect(() => {
        if (initialParams.email) {
            submitForm();
            navigate({search: ''}, {replace: true});
        }
    }, []);

    const renderResults = () => {
        if (isPending) {
            return <Loader variant='secondary' className={styles.loader} />;
        }

        if (errors.email || !isSuccessful) {
            return (
                <p className={styles.noteBlock}>
                    <strong>{fields.email.trim() === '' ? 'Empty email address' : 'Invalid email address'}.</strong> <br /><br />
                    Please enter a valid email format (e.g., example@domain.com) to check for breaches.
                </p>
            );
        }

        if (data.length === 0) {
            if (isSuccessful) {
                return (
                    <>
                        <h2 className={styles.secondaryTitle}>Breaches</h2>
                        <p className={styles.description}>
                            A <strong>breach</strong> is an incident where data has been unintentionally exposed to the public.
                        </p>
                        <p className={styles.noteBlock}>
                            <strong>Good news!</strong><br /><br />
                            <strong>No breaches found for this email.</strong><br /><br />
                            Your data appears safe, but remember to stay vigilant and use strong, unique passwords.
                        </p>
                    </>
                )
            }

            return null;
        }

        return (
            <>
                <h2 className={styles.secondaryTitle}>Breaches</h2>
                <p className={styles.description}>
                    A <strong>breach</strong> is an incident where data has been unintentionally exposed to the public.
                </p>
                <ul className={styles.resultList}>
                    {data.map(item => (
                        <li
                            key={item.name}
                            className={styles.resultListItem}
                        >
                            <div className={styles.listItemWrapper}>
                                <img
                                    alt={item.title}
                                    src={item.logoPath}
                                />
                                <span>{item.title}</span>
                            </div>
                            <p className={styles.listItemDescription} dangerouslySetInnerHTML={{__html: item.description}} />
                            <div className={styles.compromisedList}>
                                <span className={styles.compromisedListTitle}>Compromised data:</span>
                                {item.compromisedData.map((compromisedItem, index) => <span key={index} className={styles.compromisedListItem}>{compromisedItem}</span>)}
                            </div>
                        </li>
                    ))}
                </ul>
            </>
        )
    };

    const errorText = errors.email || errors.form;

    return (
        <div className={styles.root}>
            <section className={`${styles.section}${data.length > 0 ? ' ' + styles.sectionWithWarning : ''}`}>
                <div className={styles.wrapper}>
                    <h1 className={styles.title}>Email Checker</h1>
                    <p className={styles.description}>
                        Quickly scan known breaches and see where your email has been exposed.
                    </p>
                    <form className={styles.form}>
                        <Input
                            name='email'
                            onChange={changeField}
                            placeholder='Enter email'
                            value={fields.email}
                            errorText={errorText}
                            className={styles.input}
                            inputProps={{autoComplete: 'email', type: 'email'}}
                            onClear={clearForm}
                        />
                        <Button
                            className={styles.button}
                            onClick={submitForm}
                            disabled={isPending}
                            label='check it'
                        />
                    </form>
                </div>
                {!errorText && isSuccessful && (
                    <div className={styles.resultBlock}>
                        <span>Breaches:</span>
                        <span>{isPending ? <Loader variant='secondary' className={styles.smallLoader} /> : data.length || isSuccessful ? 0 : '—'}</span>
                    </div>
                )}
            </section>
            {renderResults()}
        </div>
    )
};

export {
    EmailCheckPage
};
